/**
 * The project area's pages as preloadable lazies (see lib/lazyPreload.ts),
 * and the warm-up that fetches them once a project has settled.
 *
 * The first visit to each page in a session — and to every page after a
 * deploy, when the chunk hashes change — used to swap the page the reader
 * was on for the route's "Loading…" fallback while its chunk was fetched,
 * then hold the new page back behind React's 300ms reveal throttle. Measured
 * in the harness on a landscape iPad: the fallback flashed at 40–70ms on the
 * first visit to Tasks, Availability and Offers and never on a return visit;
 * the page itself landed 60–190ms later than on the return. Warm, the route
 * renders on the tap's own commit, straight into the page's own loading
 * state for its data.
 *
 * App.tsx renders these exact components; the specifiers are the pages'.
 */
import { canWarmIdle, lazyPreload, whenIdle, type Preloadable } from "@/lib/lazyPreload";

export const ProjectPickerPage = lazyPreload(() => import("@/pages/ProjectPicker"));
export const DashboardPage = lazyPreload(() => import("@/pages/Dashboard"));
export const ContactsPage = lazyPreload(() => import("@/pages/Contacts"));
export const TasksPage = lazyPreload(() => import("@/pages/Tasks"));
export const CampaignsPage = lazyPreload(() => import("@/pages/Campaigns"));
export const OffersPage = lazyPreload(() => import("@/pages/Offers"));
export const SalesListPage = lazyPreload(() => import("@/pages/SalesList"));
export const SigningPage = lazyPreload(() => import("@/pages/Signing"));
export const AvailabilityPage = lazyPreload(() => import("@/pages/Availability"));
export const ReportsPage = lazyPreload(() => import("@/pages/Reports"));
export const DepositsPage = lazyPreload(() => import("@/pages/Deposits"));
export const ProjectSettingsPage = lazyPreload(() => import("@/pages/CurrentProjectSettings"));
export const EmailMarketingPage = lazyPreload(() => import("@/pages/EmailMarketing"));
export const WebsiteAnalyticsPage = lazyPreload(() => import("@/pages/WebsiteAnalytics"));
export const ShowroomPage = lazyPreload(() => import("@/pages/Showroom"));
export const AgentPortalPage = lazyPreload(() => import("@/pages/AgentPortal"));

/** The project navigation's `url` segment → the page it opens. */
const BY_SEGMENT: Record<string, Preloadable> = {
  dashboard: DashboardPage,
  contacts: ContactsPage,
  tasks: TasksPage,
  campaigns: CampaignsPage,
  offers: OffersPage,
  "sales-list": SalesListPage,
  signing: SigningPage,
  availability: AvailabilityPage,
  reports: ReportsPage,
  deposits: DepositsPage,
  settings: ProjectSettingsPage,
  "email-marketing": EmailMarketingPage,
  "website-analytics": WebsiteAnalyticsPage,
  showroom: ShowroomPage,
  "agent-portal": AgentPortalPage,
};

/** Fetch the page behind a project nav url ("contacts", "availability/inventory"). */
export function preloadProjectPage(url: string): void {
  const page = BY_SEGMENT[url.split("/")[0]];
  if (page) void page.preload().catch(() => {});
}

/**
 * Once the page is idle, fetch the project pages one after another — not all
 * at once, so the warm-up never competes with the reader's own requests for
 * the network or the main thread (each chunk is parsed and evaluated when it
 * lands). Cancelled if the project area is left first. Skipped on a
 * low-memory device or under Save-Data (canWarmIdle).
 */
export function warmProjectPages(): () => void {
  if (!canWarmIdle()) return () => {};
  let cancelled = false;
  const pages = Object.values(BY_SEGMENT);
  const next = async () => {
    for (const page of pages) {
      if (cancelled) return;
      try { await page.preload(); } catch { /* the route will report it */ }
    }
  };
  const stop = whenIdle(() => { void next(); });
  return () => { cancelled = true; stop(); };
}
